
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, BorderStyle, WidthType, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';

// Define font size constants with 20% increase from the standard docx sizes
// Standard docx sizes: Heading 1 = 20pt, Heading 2 = 16pt, Heading 3 = 14pt, Normal = 11pt
const DEFAULT_FONT_SIZE = 13;  // 11pt with 20% increase
const HEADING1_FONT_SIZE = 24; // 20pt with 20% increase
const HEADING2_FONT_SIZE = 19; // 16pt with 20% increase
const HEADING3_FONT_SIZE = 17; // 14pt with 20% increase

export async function generateStudentVisitHistoryDoc(student: any, visits: any[]) {
  if (!visits || visits.length === 0) {
    throw new Error("No visit history to download");
  }

  // Create document
  const doc = new Document({
    sections: [{
      properties: {},
      children: [
        new Paragraph({
          text: "Student Visit History",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          children: [
            new TextRun({
              text: "Student Visit History",
              bold: true,
              size: HEADING1_FONT_SIZE,
            })
          ],
        }),
        new Paragraph({
          text: "",
        }),
        new Paragraph({
          children: [
            new TextRun({ text: "Student Name: ", bold: true, size: DEFAULT_FONT_SIZE }),
            new TextRun({ text: `${student.surname}${student.other_names ? `, ${student.other_names}` : ""}`, size: DEFAULT_FONT_SIZE }),
          ],
        }),
        new Paragraph({
          children: [
            new TextRun({ text: "Class: ", bold: true, size: DEFAULT_FONT_SIZE }),
            new TextRun({ text: `${student.class}${student.stream ? ` Stream: ${student.stream}` : ""}`, size: DEFAULT_FONT_SIZE }),
          ],
        }),
        new Paragraph({
          text: "",
        }),
        new Paragraph({
          heading: HeadingLevel.HEADING_2,
          children: [
            new TextRun({
              text: "Visit Records",
              bold: true,
              size: HEADING2_FONT_SIZE,
            })
          ],
        }),
        new Paragraph({
          text: "",
        }),
        ...generateVisitTables(visits),
      ],
    }],
  });

  return doc;
}

function generateVisitTables(visits: any[]) {
  // Group visits by term
  const visitsByTerm = visits.reduce((acc, visit) => {
    const termInfo = visit.terms;
    const key = termInfo ? `${termInfo.name} - ${termInfo.academic_year.name}` : 'Unknown Term';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(visit);
    return acc;
  }, {} as Record<string, any[]>);

  const result: Paragraph[] = [];

  // Create tables for each term
  if (visitsByTerm && typeof visitsByTerm === 'object') {
    Object.entries(visitsByTerm).forEach(([termTitle, termVisits]) => {
      result.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_3,
          children: [
            new TextRun({
              text: termTitle,
              bold: true,
              size: HEADING3_FONT_SIZE,
            })
          ],
        }),
        new Paragraph({ text: "" })
      );

      termVisits.forEach(visit => {
        const visitDate = new Date(visit.date);
        
        // Visit date header
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Date: ", bold: true, size: DEFAULT_FONT_SIZE }),
              new TextRun({ text: visitDate.toLocaleDateString(), size: DEFAULT_FONT_SIZE }),
              new TextRun({ text: " Time: ", bold: true, size: DEFAULT_FONT_SIZE }),
              new TextRun({ text: visitDate.toLocaleTimeString(), size: DEFAULT_FONT_SIZE }),
            ],
          })
        );

        // Visit details table
        const table = new Table({
          width: {
            size: 100,
            type: WidthType.PERCENTAGE,
          },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 1 },
            bottom: { style: BorderStyle.SINGLE, size: 1 },
            left: { style: BorderStyle.SINGLE, size: 1 },
            right: { style: BorderStyle.SINGLE, size: 1 },
            insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
            insideVertical: { style: BorderStyle.SINGLE, size: 1 },
          },
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  width: {
                    size: 30,
                    type: WidthType.PERCENTAGE,
                  },
                  children: [new Paragraph({ 
                    children: [new TextRun({ text: "Category", bold: true, size: DEFAULT_FONT_SIZE })]
                  })],
                }),
                new TableCell({
                  width: {
                    size: 70,
                    type: WidthType.PERCENTAGE,
                  },
                  children: [new Paragraph({ 
                    children: [new TextRun({ text: "Details", bold: true, size: DEFAULT_FONT_SIZE })]
                  })],
                }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ 
                    children: [new TextRun({ text: "Complaint", size: DEFAULT_FONT_SIZE })]
                  })],
                }),
                new TableCell({
                  children: [new Paragraph({ 
                    children: [new TextRun({ text: visit.complaint || "N/A", size: DEFAULT_FONT_SIZE })]
                  })],
                }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ 
                    children: [new TextRun({ text: "Diagnosis", size: DEFAULT_FONT_SIZE })]
                  })],
                }),
                new TableCell({
                  children: [new Paragraph({ 
                    children: [new TextRun({ text: visit.diagnosis || "N/A", size: DEFAULT_FONT_SIZE })]
                  })],
                }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ 
                    children: [new TextRun({ text: "Temperature", size: DEFAULT_FONT_SIZE })]
                  })],
                }),
                new TableCell({
                  children: [new Paragraph({ 
                    children: [new TextRun({ text: `${visit.temperature}°C`, size: DEFAULT_FONT_SIZE })]
                  })],
                }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ 
                    children: [new TextRun({ text: "Treatment", size: DEFAULT_FONT_SIZE })]
                  })],
                }),
                new TableCell({
                  children: [new Paragraph({ 
                    children: [new TextRun({ text: visit.treatment || "N/A", size: DEFAULT_FONT_SIZE })]
                  })],
                }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ 
                    children: [new TextRun({ text: "Follow-up Required", size: DEFAULT_FONT_SIZE })]
                  })],
                }),
                new TableCell({
                  children: [new Paragraph({ 
                    children: [new TextRun({ text: visit.requires_followup ? "Yes" : "No", size: DEFAULT_FONT_SIZE })]
                  })],
                }),
              ],
            }),
          ],
        });

        result.push(table as unknown as Paragraph, new Paragraph({ text: "" }));
      });
    });
  }

  return result;
}

// Download a document
export async function downloadVisitHistory(student: any, visits: any[]) {
  try {
    const doc = await generateStudentVisitHistoryDoc(student, visits);
    const blob = await Packer.toBlob(doc);
    const studentName = `${student.surname}${student.other_names ? `_${student.other_names}` : ""}`.replace(/\s+/g, '_');
    saveAs(blob, `${studentName}_visit_history.docx`);
    return true;
  } catch (error) {
    console.error("Failed to generate document:", error);
    return false;
  }
}

// View a document using a dedicated viewer approach
export async function viewVisitHistory(student: any, visits: any[]) {
  try {
    const doc = await generateStudentVisitHistoryDoc(student, visits);
    const blob = await Packer.toBlob(doc);
    
    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
    
    // Create an iframe to display the document instead of downloading it
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    
    // Use a window.open approach for more control
    const newWindow = window.open(url, '_blank');
    
    // If pop-up was blocked, notify user
    if (!newWindow) {
      alert('Please allow pop-ups to view the document');
    }
    
    // Clean up
    document.body.removeChild(iframe);
    
    // Clean up the URL after a delay to allow browser time to open it
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    
    return true;
  } catch (error) {
    console.error("Failed to generate document for viewing:", error);
    return false;
  }
}
